import React, { createContext, useState, useContext, useEffect } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

const OfferTemplatesContext = createContext();

export const OfferTemplatesProvider = ({ children }) => {
  const [offerTemplates, setOfferTemplates] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE = "https://hrmsnew.prepstripe.com/api";
  const token = getToken();

  // Fetch all offer templates with pagination
  const fetchOfferTemplates = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE}/offer-templates?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = response.data.data;
      setOfferTemplates(responseData.data || []);
      setPagination({
        currentPage: responseData.current_page,
        totalPages: responseData.last_page,
        totalItems: responseData.total,
        perPage: responseData.per_page,
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch templates.");
    } finally {
      setLoading(false);
    }
  };

  // Create a new offer template
  const createOfferTemplate = async (templateData) => {
    try {
      const response = await axios.post(`${API_BASE}/offer-templates`, templateData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // alert(response.data.message);
      fetchOfferTemplates(pagination.currentPage); // Refresh the current page
    } catch (err) {
      throw new Error(err.response?.data?.errors?.name?.[0] || "Failed to create template.");
    }
  };

  // Update an offer template
  const updateOfferTemplate = async (id, templateData) => {
    try {
      const response = await axios.put(`${API_BASE}/offer-templates/${id}`, templateData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // alert(response.data.message);
      fetchOfferTemplates(pagination.currentPage); // Refresh the current page
    } catch (err) {
      throw new Error(err.response?.data?.message || "Failed to update template.");
    }
  };

  // Delete an offer template
  const deleteOfferTemplate = async (id) => {
    try {
      const response = await axios.delete(`${API_BASE}/offer-templates/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // alert(response.data.message);
      fetchOfferTemplates(pagination.currentPage); // Refresh the current page
    } catch (err) {
      throw new Error(err.response?.data?.message || "Failed to delete template.");
    }
  };

  useEffect(() => {
    fetchOfferTemplates(); // Fetch the first page on component mount
  }, []);

  return (
    <OfferTemplatesContext.Provider
      value={{
        offerTemplates,
        pagination,
        loading,
        error,
        fetchOfferTemplates,
        createOfferTemplate,
        updateOfferTemplate,
        deleteOfferTemplate,
      }}
    >
      {children}
    </OfferTemplatesContext.Provider>
  );
};

// Custom hook for consuming context
export const useOfferTemplates = () => useContext(OfferTemplatesContext);
